import { Input, InputProps, InputRef, Tooltip, TooltipProps } from 'antd';
import classNames from 'classnames';
import { HTMLAttributes, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CURRENCY_FORMAT } from '@common/constants';
import { rowType } from '@common/enums/estimate-section.enums';
import { isCountertopProduct } from '@common/helpers';
import formatUOM from '@common/helpers/formatUOM';
import { getUnitType } from '@common/helpers/price-helper';
import { PricedProduct } from '@common/models';
import { getProductDescription } from '@common/utils';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import { useOptionalProductContext } from '@ee-context';

import './line-item-helper.scss';

export interface PricingRowProps {
    item: PricedProduct;
    parity: number;
    hasMinimumQuantityWarning: boolean;
}

export const PricingRow = ({ item, parity, hasMinimumQuantityWarning }: PricingRowProps): JSX.Element => {
    const {
        userOptions: { showUnitPrice },
    } = useRetailerSettings();
    const unitOfMeasure = formatUOM(getUnitType(item));
    const minQtyFlag = isCountertopProduct(item) && hasMinimumQuantityWarning;

    const { selectedOptionalProducts } = useOptionalProductContext();

    const opNotes = new Map(
        Array.from(selectedOptionalProducts.entries()).map(([productId, productSelection]) => [
            productId,
            productSelection.note,
        ])
    );
    const note = opNotes.get(item.productId);

    return (
        <div className={classNames('line-item-container', { 'line-item-container--dark': parity !== 1 })}>
            <div className={'product-price-container'}>
                <div className="product-name">{getProductDescription(item)}</div>

                <div className="product-qty">
                    {item.quantity.toFixed(0)} {unitOfMeasure}
                    {minQtyFlag ? '*' : ''}
                </div>
                {showUnitPrice && (
                    <div className="product-price">{CURRENCY_FORMAT.format(item.unitPriceWithoutDiscount ?? 0)}</div>
                )}

                <div className="product-price">{CURRENCY_FORMAT.format(item.priceWithoutDiscount)}</div>
            </div>
            {note ? <div className="product-note">{note}</div> : null}
        </div>
    );
};

export enum ContentTypeEnum {
    TEXT = 'TEXT',
    INPUT = 'INPUT',
}

export interface TotalPricingRowItemProps {
    name: string;
    value: number | undefined;
    rowStyle: rowType;
    showZeroPrice?: boolean;
    contentType?: ContentTypeEnum;
    inputProps?: InputProps;
    tooltipProps?: TooltipProps;
}

export const TotalPricingRowItem = ({
    name,
    value,
    rowStyle: rowStyleType,
    showZeroPrice,
    className = '',
    contentType = ContentTypeEnum.TEXT,
    inputProps,
    tooltipProps,
    ...props
}: TotalPricingRowItemProps & HTMLAttributes<HTMLDivElement>): JSX.Element => {
    const { t } = useTranslation();
    const { className: inputClassName = '', ...restInputProps } = { ...inputProps };
    const inputRef = useRef<InputRef>(null);

    /**
     * Focus on the input field when it mounts
     */
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    return (
        <div className={`${className} sub-total-container`} {...props}>
            <div className={rowStyleType + '-name'}>{name}</div>
            <div className={rowStyleType + '-content'}>
                {contentType === ContentTypeEnum.INPUT ? (
                    <Input
                        ref={inputRef}
                        className={`${inputClassName} sub-total-container__input`}
                        onFocus={() => {
                            if (inputRef.current) {
                                inputRef.current.select();
                            }
                        }}
                        {...restInputProps}
                    />
                ) : null}
                {contentType === ContentTypeEnum.TEXT && tooltipProps ? (
                    <Tooltip {...tooltipProps}>
                        {showZeroPrice || value
                            ? CURRENCY_FORMAT.format(Number(value ?? 0))
                            : t('ORDER_SUMMARY.ESTIMATE_SECTION.ORDER.INCLUDED')}
                    </Tooltip>
                ) : (
                    <>
                        {showZeroPrice || value
                            ? CURRENCY_FORMAT.format(Number(value ?? 0))
                            : t('ORDER_SUMMARY.ESTIMATE_SECTION.ORDER.INCLUDED')}
                    </>
                )}
            </div>
        </div>
    );
};
